/**
 * @lume/shared — Categorias NEUTRAS de risco de ação + pisos (Fatia A2 — Porteiro Neutro).
 *
 * Vocabulário fixo de 5 categorias puras (VEREDITO) + piso por categoria + piso
 * estrutural de CONSEQUÊNCIA (agnóstico de caminho MCP/nativo/confirm — P12). DORMENTE
 * em A2: nada consome no caminho quente ⇒ byte-idêntico por construção (como A1).
 *
 * Modelo composto (A3+): decisão = APERTA(pisoEstrutural, pisoCategoria, resolveAutoria).
 * A2 entrega os DOIS pisos (código-piso) + o mapa seed 17→5; `resolveAutoria` (config,
 * mais-específico-vence, seed = requiredLevel de hoje) é A3. A byte-identidade sai do
 * INVARIANTE `resolveStructuralFloor ≤ requiredLevel de hoje` (differential): como
 * APERTA=max e o piso ≤ seed, `max(piso, seed)=seed` ⇒ o gate não muda.
 *
 * NEUTRALIDADE: os nomes das categorias são universais; A2 só CATEGORIZA os 17
 * `IntentAction` pré-existentes (cujos nomes telecom saem em A7/A3).
 *
 * ★ CONSTRAINT A6 (RESOLVIDA na fatia A6, `access-decision.ts`): o subset
 * `IdentityGateResult` ganhou `role`+`assuranceOk` e `deriveAccessDecision` separa
 * `needs_identity` (incl. `unknown`-não-verificado, OTP-resolvível) de `deny`. A2 (só o
 * eixo do NÍVEL/categoria) não toca papel.
 */
import { isIntentAction } from './intent-actions';
import { assuranceRank } from './index';
/** As 5 categorias puras (VEREDITO). `life-safety`/`escalation` NÃO entram (protection-triggers). */
export const RISK_CATEGORIES = [
    'none',
    'read-pii',
    'write-financial',
    'irreversible',
    'regulated-data',
];
/** APERTA — devolve o nível MAIOR (só endurece; piso nunca abaixa). */
export function tightenAssurance(a, b) {
    return assuranceRank(a) >= assuranceRank(b) ? a : b;
}
/**
 * Piso (nível MÍNIMO de identidade) de uma categoria. Código-PISO: só endurece.
 * `read-pii`/`regulated-data` = verified (proteção primária das leituras, spec §3.3);
 * `irreversible` soft/hard; `write-financial` graduado por valor; `none` = sem piso.
 *
 * **Fail-safe conservador nas facetas (Fable I3):** faceta não-declarada cai no MAIS
 * rígido (mesma doutrina do fail-safe invertido de `isConsequentialAction`): severidade
 * ausente ⇒ `hard`/verified; valor presente com limiar AUSENTE ⇒ verified (calibração
 * desconhecida = conservador). Só `soft` explícito → self_declared. DORMENTE hoje (o seed
 * declara `soft` explícito nas 6; nada alimenta valor — PA9) ⇒ zero impacto de byte-identidade.
 */
export function categoryFloor(category, opts) {
    switch (category) {
        case 'none':
            return 'none';
        case 'read-pii':
        case 'regulated-data':
            return 'verified';
        case 'irreversible':
            // só `soft` explícito rebaixa; não-declarado/`hard` ⇒ conservador (verified).
            return opts?.severity === 'soft' ? 'self_declared' : 'verified';
        case 'write-financial': {
            const v = opts?.financialValue;
            const t = opts?.financialThreshold;
            if (v == null)
                return 'self_declared'; // sem valor da operação → piso base
            if (t == null)
                return 'verified'; // valor sem limiar (calibração ausente) → conservador
            return v >= t ? 'verified' : 'self_declared';
        }
    }
}
/**
 * Ações CONSEQUENTES (seed) → severidade da consequência. Comprometem o recurso FINAL /
 * mutam registro durável / movem dinheiro — medido pelo que a ação FAZ, agnóstico de
 * caminho (P12). Inclui os writes NATIVOS (ToolKind null) que uma derivação por
 * `isMutationToolKind` PERDERIA.
 *
 * **Severidade (A3, decisão do TL 21/Jul — Fable A2 I4):** `cadastral_change` = **hard**
 * (account-takeover — mudar endereço redireciona correspondência/equipamento) ⇒ piso
 * `verified` INAFROUXÁVEL por config (a consequência é APERTA'da sempre; imune a
 * rebaixamento de CATEGORIA). Os demais = **soft** (`self_declared`) — preserva o
 * rebaixamento comercial de `service_action` (PA2). A proteção forte mora em "o que a
 * ação FAZ" (Decisão 2), não na etiqueta que o tenant escolhe.
 *
 * **O que NÃO é consequente (Fable I1):** uma posição REVERSÍVEL numa fila NÃO conta,
 * mesmo que a fila seja CAPADA (`agenda_join_waitlist` → `WaitlistEntry`, com
 * `maxQueueSize`+lock em `waitlist.service.ts`). O recurso FINAL (o slot) só é
 * comprometido ao CRAVAR (`agenda_book`, `verified`). A escassez da fila é um vetor de
 * FLOODING — mitigado por rate-limit/cap na waitlist, NÃO por piso de identidade (é um
 * residual distinto, declarado).
 */
export const CONSEQUENTIAL_ACTIONS = {
    cadastral_change: 'hard',
    service_action: 'soft',
    appointment_confirm: 'soft',
    agenda_book: 'soft',
    agenda_reschedule: 'soft',
    agenda_cancel: 'soft',
};
const CONSEQUENTIAL_KEYS = new Set(Object.keys(CONSEQUENTIAL_ACTIONS));
/**
 * A ação é consequente? Conhecida (dos 17) → membership. **DESCONHECIDA ⇒ true**
 * (fail-safe INVERTIDO — corrige a polaridade errada de `isMutationToolKind`; no mundo
 * neutro, registrar ação EXIGE declarar a consequência, e o não-declarado cai no conservador).
 */
export function isConsequentialAction(action) {
    if (!isIntentAction(action))
        return true;
    return CONSEQUENTIAL_KEYS.has(action);
}
/**
 * Piso estrutural de consequência (INAFROUXÁVEL — config só ENDURECE por cima):
 * não-consequente → `none`; consequente `soft` → `self_declared`; consequente `hard`
 * → `verified`. Ação DESCONHECIDA consequente (fail-safe invertido) sem severidade →
 * `self_declared` (conservador base A2; o registro de ação nova declara a severidade — A7).
 */
export function consequenceFloor(action) {
    if (!isConsequentialAction(action))
        return 'none';
    const severity = isIntentAction(action)
        ? CONSEQUENTIAL_ACTIONS[action]
        : undefined;
    return severity === 'hard' ? 'verified' : 'self_declared';
}
/**
 * O BACKSTOP de consequência (A4 — porteiro): APERTA o piso estrutural de consequência
 * (`consequenceFloor`, inafrouxável — Decisão 2) sobre o nível autorado da ação. Fonte
 * ÚNICA e agnóstica de caminho (P12) da detecção de consequência para o PISO DE IDENTIDADE
 * — as 3 famílias de escrita (MCP/nativo/confirm) herdam-na via `evaluateIdentityGate`.
 *
 * **Byte-idêntico hoje:** `consequenceFloor(action) ≤ autorado` ∀ ação (differential A2)
 * ⇒ `tightenAssurance(consequenceFloor, autorado) = autorado`. Morde só quando um autorado
 * futuro (promoção da casa de config A3, ou override novo) cair ABAIXO do piso — aí o gate
 * segura o piso, imune a config. `null` (out_of_scope) passa direto.
 *
 * NÃO é `resolveStructuralFloor` (que soma o piso de CATEGORIA, config/rebaixável — Decisão 2);
 * A4 fia só o piso de CONSEQUÊNCIA (código-piso puro). A categoria + a casa de config = promoção.
 *
 * **Autorado `null` NÃO curto-circuita antes do piso (Fable A4 M1):** se um dia uma ação
 * CONSEQUENTE for autorada com nível `null` (ex.: A7), o piso ainda MORDE (fail-safe, molde do
 * fail-safe-invertido de `isConsequentialAction`). Hoje só `out_of_scope` é null e é
 * NÃO-consequente (piso `none`) ⇒ retorna `null` ⇒ byte-idêntico (`assuranceAtLeast(·,none)=true`).
 */
export function applyConsequenceFloor(authoredLevel, action) {
    const floor = consequenceFloor(action);
    if (authoredLevel === null)
        return floor === 'none' ? null : floor;
    return tightenAssurance(floor, authoredLevel);
}
const A = (category, severity) => severity !== undefined ? { category, severity } : { category };
/**
 * Mapa seed 17→5 (config-as-code, molde `intent-actions.ts`). Vira o SEED default de
 * plataforma da casa de config de A3. Byte-idêntico: os pisos daqui nunca excedem o
 * `requiredLevel` de hoje (differential prova).
 */
export const ACTION_RISK_CATEGORY = {
    public_info: A('none'),
    account_context: A('none'), // self_declared vem do autorado (config), não de piso de categoria
    sensitive_data_read: A('read-pii'),
    billing_payment_code_read: A('read-pii'),
    service_status_read: A('read-pii'),
    service_connection_read: A('read-pii'),
    service_ticket_read: A('read-pii'),
    service_slots_query: A('read-pii'),
    cadastral_change: A('irreversible', 'hard'), // A3/TL 21/Jul: account-takeover = hard (categoryFloor verified, coerente c/ consequência hard; proteção inafrouxável vem da consequência)
    service_action: A('irreversible', 'soft'),
    appointment_confirm: A('irreversible', 'soft'),
    agenda_availability_query: A('none'),
    agenda_book: A('irreversible', 'soft'),
    agenda_reschedule: A('irreversible', 'soft'),
    agenda_cancel: A('irreversible', 'soft'),
    agenda_join_waitlist: A('none'), // preferência reversível — NÃO consequente
    out_of_scope: A('none'), // escala; requiredLevel null (differential pula ≤)
};
/**
 * Override esparso de categoria por vertical (molde `INTENT_ACTION_SCOPE_BY_VERTICAL`).
 * Exercita `regulated-data` (dado clínico/jurídico é regulado). Piso `verified` em ambos
 * ⇒ byte-idêntico; só torna a 5ª categoria viva.
 */
export const ACTION_RISK_CATEGORY_BY_VERTICAL = {
    healthcare: { sensitive_data_read: A('regulated-data') },
    legal: { sensitive_data_read: A('regulated-data') },
};
/** Resolve a categoria de uma ação no vertical (override esparso → default). */
export function categoryForAction(action, vertical) {
    if (vertical != null && vertical in ACTION_RISK_CATEGORY_BY_VERTICAL) {
        const override = ACTION_RISK_CATEGORY_BY_VERTICAL[vertical][action];
        if (override !== undefined)
            return override;
    }
    return ACTION_RISK_CATEGORY[action];
}
/**
 * Piso ESTRUTURAL de uma ação = APERTA(pisoConsequência, pisoCategoria). É a parte
 * código-PISO do modelo composto (A3 soma `resolveAutoria` config por cima com APERTA).
 * Tipado a `IntentAction` (os 17); o mapeamento categoria-para-ação-DESCONHECIDA é A3
 * (registro declarativo).
 *
 * **RESIDUAIS honestos (Decisão 2 — não fecham):** (leitura) uma leitura de PII
 * mal-categorizada como `none` passa — não há detector estrutural de leitura (só a
 * categoria declarada + Nível 4 + portão de aprovação de A3/Frente D); é o elo mais
 * fraco. (ESCRITA — espelho, Fable I4) com todas as escritas floored só em
 * `self_declared`, proteção de escrita ACIMA disso também depende do autorado/config —
 * ex.: `cadastral_change` (vetor de account-takeover: endereço redireciona
 * correspondência/equipamento) é `verified` HOJE por autoria, não por piso. Elevar o
 * piso de `cadastral_change` a `verified` (via severidade `hard`) é byte-ZERO
 * (`cadastral_change` é `verified` em todos os verticais) — **decisão de PRODUTO do TL**
 * (subir piso ≠ mandato de A2; Decisão 2 = pisos baixos + tenant só endurece).
 *
 * **Fail-safe (Fable M5):** ação DESCONHECIDA em runtime (cast fora dos 17, ex.: output
 * de modelo em A3) degrada ao piso de consequência (self_declared) em vez de LANÇAR —
 * uma camada de fail-safe não deve ter caminho que crasha.
 */
export function resolveStructuralFloor(action, vertical, opts) {
    if (!isIntentAction(action))
        return consequenceFloor(action);
    const assignment = categoryForAction(action, vertical);
    // Construção condicional (exactOptionalPropertyTypes): nunca passar `undefined` explícito.
    const floorOpts = {
        ...(assignment.severity !== undefined ? { severity: assignment.severity } : {}),
        ...(opts?.financialValue !== undefined ? { financialValue: opts.financialValue } : {}),
        ...(opts?.financialThreshold !== undefined
            ? { financialThreshold: opts.financialThreshold }
            : {}),
    };
    return tightenAssurance(consequenceFloor(action), categoryFloor(assignment.category, floorOpts));
}
//# sourceMappingURL=risk-category.js.map